import React, { useState, useEffect } from "react";
import * as Icon from "react-feather";
import {
  Button,
  Form,
  Input,
  Switch,
  Modal,
  Tabs,
  Col,
  Select,
  Tag,
} from "antd";
import { useDispatch } from "react-redux";
import newService from "../../services/new-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import { UploadOutlined } from "@ant-design/icons";
import UploadImage from "../../components/upload/image";
import UploadFile from "../../components/upload/file";
import EditableTagGroup from "../../components/input/editable-tag-group";
import {
  fileTYPE_IMG,
  groupType,
  fileTYPE_DOCUMENT,
  PREVIEW_TYPE,
} from "../../constants/app-constants";
import fileUtils from "../../utils/file-utils";
import HtmlEditor from "../../components/input/html-editor";
import { FORM_LANG, formText, imageSizeHint, langOfTab } from '../../constants/form-i18n';
import { VISIBILITY_DEFAULT } from "../../constants/app-constants";
import VisibilityFormItem from "../../components/input/visibility-form-item";
import { openWebsitePreview } from "../../utils/preview-utils";

const { TextArea } = Input;
const { TabPane } = Tabs;

const defaultValue = {
  serviceNewsImage: null,
  serviceNewsFile: [],
  isOpenNewTab: false,
  keywordList: [],
};

const AddEditNewsDialog = ({
  isShow,
  onCancel,
  onSaveComplete,
  editData,
  content,
  renderNewsTypeOption,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [serviceNewsImage, setServiceNewsImage] = useState(
    defaultValue.serviceNewsImage
  );
  const [serviceNewsFile, setServiceNewsFile] = useState(
    defaultValue.serviceNewsFile
  );
  const [keywordList, setKeywordList] = useState(defaultValue.keywordList);
  const [editorContentTH, setEditorContentTH] = useState("");
  const [editorContentEN, setEditorContentEN] = useState("");
  const [newType, setNewType] = useState();
  // NMU-444: the active tab drives the language of the whole dialog.
  const [lang, setLang] = useState(FORM_LANG.TH);
  const t = formText(lang);

  useEffect(() => {
    if (!isShow) {
      setServiceNewsImage(defaultValue.serviceNewsImage);
      setKeywordList(defaultValue.keywordList);
    }
  }, [isShow]);

  useEffect(() => {
    if (isShow && editData) {
      form.setFieldsValue({
        titleTH: editData.title.titleTH,
        titleEN: editData.title.titleEN,
        descriptionTH: editData.shortDescription.descriptionTH,
        descriptionEN: editData.shortDescription.descriptionEN,
        status: editData.status,
        totalView: editData.totalView,
        facebookLink: editData.facebookLink,
        urlSuffix: editData.urlSuffix,
        newsType: editData.newsType,
        visibility: editData.visibility ? editData.visibility : VISIBILITY_DEFAULT,
      });

      setServiceNewsImage(editData.attachImage);
      setServiceNewsFile(editData.documentList);
      setKeywordList(
        editData.keywordList ? editData.keywordList : defaultValue.keywordList
      );
      setEditorContentTH(editData.content.contentTH);
      setEditorContentEN(editData.content.contentEN);
    } else {
      form.setFieldsValue({
        titleTH: null,
        titleEN: null,
        descriptionTH: null,
        descriptionEN: null,
        status: true,
        totalView: 0,
        facebookLink: null,
        urlSuffix: null,
        newsType: null,
        visibility: VISIBILITY_DEFAULT,
      });
      setServiceNewsFile([]);
      setEditorContentTH("");
      setEditorContentEN("");
    }
  }, [isShow, editData, form]);

  const props = {
    action: "//jsonplaceholder.typicode.com/posts/",
    listType: "picture",
    multiple: false,
    serviceNewsFile: serviceNewsFile,
  };

  const onChangeValueTH = (value) => {
    setEditorContentTH(value);
  };

  const onChangeValueEN = (value) => {
    setEditorContentEN(value);
  };

  const onChangeNewsType = (value) => {
    setNewType(value);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      try {
        dispatch(appLoaderAction.enable());

        let bean = getSaveBean(values);
        if (!editData) {
          await newService.add(bean);
        } else {
          await newService.edit(bean);
        }

        dispatch(appLoaderAction.disale());

        onSaveComplete();

        notifyUtitls.success(t('successTitle'), t('saveSuccess'));
      } catch (err) {
        notifyUtitls.error(err);
        dispatch(appLoaderAction.disale());
      }
    } catch (errorInfo) {
      notifyUtitls.warning(t('warningTitle'), t('incompleteForm'));
    }
  };

  function getSaveBean(values) {
    if (!editData) {
      let bean = {
        bean: {
          title: {
            titleTH: values.titleTH,
            titleEN: values.titleEN,
          },
          content: {
            contentTH: editorContentTH,
            contentEN: editorContentEN,
          },
          shortDescription: {
            descriptionTH: values.descriptionTH,
            descriptionEN: values.descriptionEN,
          },
          keywordList: keywordList,
          attachImage: serviceNewsImage,
          documentList: serviceNewsFile,
          totalView: 0,
          status: values.status,
          facebookLink: values.facebookLink,
          urlSuffix: values.urlSuffix,
          newsType: values.newsType,
          visibility: values.visibility,
        },
      };

      return bean;
    } else {
      let bean = JSON.parse(JSON.stringify(editData));
      bean.title.titleTH = values.titleTH;
      bean.title.titleEN = values.titleEN;
      bean.content.contentTH = editorContentTH;
      bean.content.contentEN = editorContentEN;
      bean.shortDescription.descriptionTH = values.descriptionTH;
      bean.shortDescription.descriptionEN = values.descriptionEN;
      bean.keywordList = keywordList;
      bean.attachImage = serviceNewsImage;
      bean.status = values.status;
      bean.totalView = editData.totalView;
      bean.documentList = serviceNewsFile;
      bean.facebookLink = values.facebookLink;
      bean.urlSuffix = values.urlSuffix;
      bean.newsType = values.newsType;
      bean.visibility = values.visibility;
      const editResult = {
        bean: {
          ...bean,
        },
      };
      return editResult;
    }
  }

  const onRemoveItem = (index) => {
    return () => {
      const newList = [...serviceNewsFile];
      newList.splice(index, 1);
      setServiceNewsFile(newList);
    };
  };

  const onUploadTemp = ({ file }) => {
    if (file.status === "done") {
      const { bean } = file.response;
      setServiceNewsImage(bean);
    }
  };

  const onUploadFile = ({ file }) => {
    if (file.status === "done") {
      const { bean } = file.response;
      setServiceNewsFile([...serviceNewsFile, bean]);
    }
  };

  const onPreview = async () => {
    try {
      dispatch(appLoaderAction.enable());

      // dispatch(mainContentPreviewAction.addMainContentPreview(getBean()));

      const values = await form.validateFields();
      let previewBean = getSaveBean(values).bean;

      await openWebsitePreview({
        type: PREVIEW_TYPE.news,
        bean: previewBean,
        path: (token) => `/news/${previewBean.newsId ? previewBean.newsId : token}/`,
      });
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  return (
    <div className="container h-100">
      <div className="title-page">
        <Modal
          title={t(editData ? 'editNews' : 'addNews')}
          centered
          width={750}
          visible={isShow}
          onCancel={onCancel}
          destroyOnClose
          footer={[
            <Button key="submit" type="primary" onClick={onSave}>
              <Icon.Save className="me-2" size={17} />
              {t('save')}
            </Button>,
            <Button key="back" className="btn-gray" onClick={onCancel}>
              {t('cancel')}
            </Button>,
            <Button className="mx-2 btn-line" onClick={() => onPreview()}>
              <Icon.Eye className="me-1" size={18} /> {t('previewWebsite')}
            </Button>,
          ]}
        >
          <Form
            layout="vertical"
            name="add_edit_user"
            preserve={false}
            form={form}
            initialValues={{ status: true, visibility: VISIBILITY_DEFAULT }}
            validateMessages={{
              required: t('required'),
            }}
          >
            <Tabs defaultActiveKey="1" onChange={(key) => setLang(langOfTab(key))}>
              <TabPane tab={t('tabThai')} key="1" forceRender>
                <Form.Item
                  label={t('newsTitle')}
                  name="titleTH"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label={t('description')}
                  name="descriptionTH"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <TextArea />
                </Form.Item>
                <Form.Item
                  label={t('newsContent')}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <div>
                    {/* NMU-441: the news form shows the rich text editor only —
                        same opt-out the activity form took in NMU-427. */}
                    <HtmlEditor
                      lang={lang}
                      key="th"
                      id="th"
                      showGalleryButtons={false}
                      setContents={editorContentTH}
                      onChange={onChangeValueTH}
                    />
                  </div>
                  {/* <TextArea /> */}
                </Form.Item>
              </TabPane>
              <TabPane tab={t('tabEnglish')} key="2" forceRender>
                <Form.Item
                  label="Title"
                  name="titleEN"
                  className="mb-4"
                  rules={[
                    {
                      required: false,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Description"
                  name="descriptionEN"
                  className="mb-4"
                  rules={[
                    {
                      required: false,
                    },
                  ]}
                >
                  <TextArea />
                </Form.Item>
                <Form.Item
                  label="Content"
                  name="contentEN"
                  className="mb-4"
                  rules={[
                    {
                      required: false,
                    },
                  ]}
                >
                  <HtmlEditor
                      lang={lang}
                    key="en"
                    id="en"
                    showGalleryButtons={false}
                    setContents={editorContentEN}
                    onChange={onChangeValueEN}
                  />
                  {/* <TextArea /> */}
                </Form.Item>
              </TabPane>
            </Tabs>

            <Form.Item label={t('attachment')} name="fileHeader">
              <UploadFile
                {...props}
                accept={fileTYPE_DOCUMENT}
                onChange={onUploadFile}
                children={serviceNewsFile}
              >
                <Button type="dashed" icon={<UploadOutlined />}>
                  Upload File
                </Button>
              </UploadFile>

              {editData && serviceNewsFile
                ? serviceNewsFile.map((file, index) => {
                    return (
                      <div>
                        <a
                          href={fileUtils.getFileRenderUrl(
                            groupType.NEWS,
                            editData ? editData.newsId : null,
                            file?.originalFilename
                          )}
                          download
                        >
                          {file?.originalFilename}
                        </a>

                        <a
                          key={"submit" + index}
                          type="danger"
                          style={{ minWidth: 10, paddingLeft: 5 }}
                          onClick={onRemoveItem(index)}
                        >
                          <Icon.Trash size={18} />
                        </a>
                      </div>
                    );
                  })
                : null}
            </Form.Item>

            <Form.Item
              label={t('image')}
              valuePropName="fileList"
              name="imageHeader"
              rules={[
                {
                  required: serviceNewsImage ? false : true,
                  // NMU-532: a picture is uploaded, not typed.
                  message: 'กรุณาอัปโหลดรูปภาพ',
                },
              ]}
            >
              <UploadImage
                listType="picture-card"
                accept={fileTYPE_IMG}
                showUploadList={false}
                onChange={onUploadTemp}
                className={serviceNewsImage ? "view-upload" : null}
              >
                {serviceNewsImage ? (
                  <img
                    src={
                      serviceNewsImage.tempFilename
                        ? fileUtils.getTempFileRenderUrl(
                            serviceNewsImage.tempFilename,
                            serviceNewsImage.type
                          )
                        : fileUtils.getFileRenderUrl(
                            groupType.NEWS,
                            editData ? editData.newsId : null,
                            serviceNewsImage.originalFilename
                          )
                    }
                    alt="avatar"
                    style={{ width: "100%" }}
                  />
                ) : (
                  t('uploadFile')
                )}
              </UploadImage>
              <label className="text-danger">{imageSizeHint(lang, '930*430 pixels')}</label>
            </Form.Item>

            <Form.Item label={t('keywords')}>
              <EditableTagGroup onChange={setKeywordList} tags={keywordList} lang={lang} />
            </Form.Item>

            <Form.Item label="FacebookLink" name="facebookLink">
              <Input />
            </Form.Item>

            <Form.Item label="URL Name" name="urlSuffix">
              <Input />
            </Form.Item>

            <Form.Item label={t('status')} name="status" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item
              label={t('newsType')}
              name="newsType"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select customWidth="100%">{renderNewsTypeOption()}</Select>
            </Form.Item>

            {/* NMU-411: news is Common now — the legacy user-group selector is removed. */}

            <VisibilityFormItem lang={lang} />
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default AddEditNewsDialog;
